import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "../../Buttons/Button";
import { useController } from "react-hook-form";
import { useTranslationApp } from "../../../lib/i18next";
import { getReactHookFormNestedError } from "../../../utils/errors";
import WebpagesTable from "./WebpagesTable";
import WebpageModal from "./WebpageModal";
import { getUniqueID } from "../../../utils/numbers";
import {
  GOOGLE_WEBPAGE_ADD_TITLE,
  GOOGLE_WEBPAGE_CRITERIA_TITLE,
} from "../../../i18n/keysTranslations";
import { ADS_GOOGLE_WEBPAGE_CRITERION_URL_EQUALS_OPTION } from "../../../utils/constants";

const WebpagesGoogleContainer = ({
  name,
  control,
  errors,
  rules,
  disabled = false,
  afterTitle = null,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingWebpage, setEditingWebpage] = useState(null);
  const { t } = useTranslationApp();

  const {
    field: { value: webpages = [], onChange: onWebpagesChange },
  } = useController({ name, control, rules });

  const error = getReactHookFormNestedError(errors, name);

  const onAddWebpage = (url) => {
    onWebpagesChange([
      ...webpages,
      {
        id: getUniqueID(),
        operator: ADS_GOOGLE_WEBPAGE_CRITERION_URL_EQUALS_OPTION,
        value: url,
      },
    ]);
  };

  const onEditWebpage = (url) => {
    onWebpagesChange(
      webpages.map((wp) =>
        wp.id === editingWebpage.id ? { ...wp, value: url } : wp
      )
    );
    setEditingWebpage(null);
  };

  const onDeleteWebpages = (ids) => {
    onWebpagesChange(webpages.filter((wp) => !ids.includes(wp.id)));
  };

  const openEditModal = (webpage) => {
    setEditingWebpage(webpage);
    setModalOpen(true);
  };

  const onCloseModal = () => {
    setModalOpen(false);
    setEditingWebpage(null);
  };

  return (
    <Box id={name}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          mt: 1,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: 600, fontSize: "18px" }}
          >
            {t(GOOGLE_WEBPAGE_CRITERIA_TITLE)}
          </Typography>
          {afterTitle && <Box sx={{ mb: 1 }}>{afterTitle}</Box>}
        </Box>
        {!disabled && (
          <Button color="secondary" onClick={() => setModalOpen(true)}>
            {t(GOOGLE_WEBPAGE_ADD_TITLE)}
          </Button>
        )}
      </Box>

      <WebpagesTable
        webpages={webpages}
        onEdit={openEditModal}
        onDelete={onDeleteWebpages}
        disabled={disabled}
      />

      {modalOpen && (
        <WebpageModal
          modalOpen={modalOpen}
          onCloseModal={onCloseModal}
          onSubmit={editingWebpage ? onEditWebpage : onAddWebpage}
          currentWebpages={webpages}
          initialData={editingWebpage}
        />
      )}

      {error?.message && (
        <Typography variant="caption" color="error" name={name}>
          {error.message}
        </Typography>
      )}
    </Box>
  );
};

export default WebpagesGoogleContainer;
